import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { commonPDFStyles } from '../styles/pdfStyles';
import { typographyStyles, specializedTypography } from '../styles/typography';
import { createT, hasTenantOverride } from '../utils/simpleTranslate';
import { formatBirthdateForPDF, formatDateForPDF } from '../utils/dataFormatters';
import { yearsFromDate, getHealthIDLabel, getPatientID, birthdateToDate } from '../../../utils';

/**
 * PDF Patient Info Component
 * 
 * Replaces PatientInfoSection component with @react-pdf/renderer equivalent.
 * Displays patient information in a single column layout.
 */

interface IPatient {
  id?: string;
  personalData?: {
    name?: string;
    surnames?: string;
    birthdate?: string | Date;
    sex?: string;
    phone?: string;
    insurance?: string | number;
    healthId?: string;
    health_id?: string;
    automated_health_id?: string;
    nationalId?: string;
    passportNumber?: string;
  };
}

interface PDFPatientInfoProps {
  patient: IPatient;
  locale?: string;
  patientCoverage?: { name: string } | null;
  translations?: {
    name?: string;
    birthdate?: string;
    age?: string;
    sex?: string;
    healthId?: string;
    insurance?: string;
    coverage?: string;
    phone?: string;
  };
}

export const PDFPatientInfo: React.FC<PDFPatientInfoProps> = ({
  patient,
  locale = 'fr',
  patientCoverage,
  translations,
}) => {
  const t = createT(locale);
  
  // Create single column layout - width is controlled by parent container
  const singleColumnLayout = {
    container: {
      width: '100%',
    },
  };
  
  // Use translations from props or defaults
  const labels = {
    name: translations?.name || t('general.full_name'),
    birthdate: translations?.birthdate || t('investigation.create.personal_data.fields.birthdate'),
    age: translations?.age || t('general.age'),
    sex: translations?.sex || t('investigation.create.personal_data.fields.sex'),
    coverage: translations?.coverage || t('investigation.create.personal_data.fields.coverage'),
    phone: translations?.phone || t('investigation.create.personal_data.fields.phone'),
  };
  
  // Get health ID label dynamically based on patient data
  const healthIdLabel = translations?.healthId || t(getHealthIDLabel(patient));

  if (!patient?.personalData) {
    return (
      <Text style={specializedTypography.statusInactive}>
        {t('patient.info_not_available')}
      </Text>
    );
  }

  const { personalData } = patient;

  return (
    <View style={singleColumnLayout.container}>
      {/* Patient Name */}
      <View style={{ marginBottom: 3 }}>
        <Text>
          <Text style={specializedTypography.fieldLabel}>
            {labels.name}:
          </Text>
          <Text style={specializedTypography.patientName}>
            {' '}{personalData.name} {personalData.surnames}
          </Text>
        </Text>
      </View>

      {/* Birthdate */}
      {personalData.birthdate && (
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.birthdate}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{formatBirthdateForPDF(personalData.birthdate, locale)}
            </Text>
          </Text>
        </View>
      )}

      {/* Sex */}
      {personalData.sex && (
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.sex}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{personalData.sex === "Male" ? t('general.male') : t('general.female')}
            </Text>
          </Text>
        </View>
      )}

      {/* Health ID */}
      <View style={{ marginBottom: 3 }}>
        <Text>
          <Text style={specializedTypography.fieldLabel}>
            {healthIdLabel}:
          </Text>
          <Text style={specializedTypography.patientId}>
            {' '}{getPatientID(patient)}
          </Text>
        </Text>
      </View>

      {/* Dossier number (SHER-371). Shown IN ADDITION to the NDM line above, and
          ONLY for tenants whose literals overlay gives `health_id` its own name
          (e.g. CHOM Dakar's "Dossier CHOM") — `hasTenantOverride` keeps this scoped
          to those tenants via the tenant file, with no hard-coded slug here.
          Gated on both IDs existing: getPatientID/getHealthIDLabel above resolve to
          the NDM when present, so a patient with no NDM already has their dossier
          rendered by that block and a second line here would duplicate it. */}
      {personalData.automated_health_id &&
        personalData.health_id &&
        hasTenantOverride('investigation.create.personal_data.fields.health_id') && (
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {t('investigation.create.personal_data.fields.health_id')}:
            </Text>
            <Text style={specializedTypography.patientId}>
              {' '}{personalData.health_id}
            </Text>
          </Text>
        </View>
      )}

      {/* Coverage */}
      {patientCoverage && (
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.coverage}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{patientCoverage.name}
            </Text>
          </Text>
        </View>
      )}



      {/* Age */}
      {personalData.birthdate && (
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.age}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{personalData.birthdate ? yearsFromDate(birthdateToDate(personalData.birthdate)) : ''} {t('general.years')}
            </Text>
          </Text>
        </View>
      )}
    </View>
  );
};

export default PDFPatientInfo;
